import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { rockRealOrFakeApi } from "../services/rockRealOrFakeApi";
import PaginationBar from "../components/common/PaginationBar";
import ErrorAlert from "../components/common/ErrorAlert";
import RealOrFakeDetailModal from "../components/realorfake/RealOrFakeDetailModal";

const ITEMS_PER_PAGE = 50;

export default function RealOrFakePage() {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);

  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await rockRealOrFakeApi.getList({ page, items_per_page: ITEMS_PER_PAGE });
        if (!cancelled) {
          setItems(data?.items ?? []);
          setTotal(data?.objects?.total ?? null);
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [page]);

  const totalPages = useMemo(() => {
    if (typeof total === "number" && ITEMS_PER_PAGE > 0) {
      return Math.ceil(total / ITEMS_PER_PAGE);
    }
    return null;
  }, [total]);

  const canNext = useMemo(() => {
    if (totalPages !== null) return page < totalPages;
    return items.length >= ITEMS_PER_PAGE;
  }, [totalPages, page, items]);

  return (
    <div className="page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">Gems</div>
              <h2 className="page-title">Real or Fake</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <ErrorAlert error={error} title="Failed to load data" />

          <Card>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="table-vcenter card-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}></th>
                      <th>Name</th>
                      <th>Latin Name</th>
                      <th style={{ width: 160 }}>Updated</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          No items found.
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => (
                        <tr
                          key={item.uid}
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedItem(item)}
                        >
                          <td>
                            {item.main_image_url ? (
                              <img
                                alt=""
                                src={item.main_image_url}
                                style={{
                                  width: 40,
                                  height: 40,
                                  objectFit: "cover",
                                  borderRadius: 6,
                                  border: "1px solid var(--tblr-border-color)"
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 6,
                                  background: "var(--tblr-bg-surface-secondary)"
                                }}
                              />
                            )}
                          </td>
                          <td className="fw-semibold">{item.common_name}</td>
                          <td className="text-muted fst-italic">{item.latin_name}</td>
                          <td className="text-muted small">
                            {item.updated_at
                              ? new Date(item.updated_at).toLocaleDateString()
                              : "—"}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => setSelectedItem(item)}
                            >
                              Detail
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer>
              <Row className="align-items-center gy-2">
                <Col>
                  <div className="text-muted small">
                    Page <b>{page}</b>
                    {typeof total === "number" ? (
                      <>
                        {" "}· Total <b>{total}</b>
                      </>
                    ) : null}
                  </div>
                </Col>
                <Col xs="auto">
                  <PaginationBar
                    page={page}
                    onPageChange={(p) => setPage(Math.max(1, p))}
                    canPrev={page > 1}
                    canNext={canNext}
                    showPageLabel={false}
                    totalPages={totalPages}
                  />
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </div>
      </div>

      <RealOrFakeDetailModal
        show={!!selectedItem}
        onHide={() => setSelectedItem(null)}
        uid={selectedItem?.uid}
        title={selectedItem?.common_name}
      />
    </div>
  );
}
